import * as XLSX from "xlsx";
import { ExcelGeneratorService } from "./excel-generator.service";
import { AddressMatcherService } from "@/services/address/address-matcher.service";
import { AddressMatch, ExcelFile } from "@/types/excel.types";
import { QSO } from "@/types/qso.types";
import { CONSTANTS } from "@/config/constants";
import { chunkArray } from "@/utils/string.utils";
import { getTimestamp } from "@/utils/date.utils";
import { checkCallInAddress } from "@/utils/string.utils";

export class AddressExcelService {
  private generator = new ExcelGeneratorService();
  private matcher = new AddressMatcherService();

  async generateAddressExcel(qsos: QSO[]): Promise<ExcelFile[]> {
    const matches = await this.matcher.matchAddresses(qsos);
    const batches = chunkArray<AddressMatch>(matches, CONSTANTS.BATCH_SIZE);
    const files: ExcelFile[] = [];

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (!batch) continue;

      const workbook = this.createWorkbook(batch);
      const buffer = this.generator.writeWorkbook(workbook);

      if (buffer.byteLength > CONSTANTS.MAX_EXCEL_FILE_SIZE) {
        const smallerBatches = chunkArray<AddressMatch>(batch, CONSTANTS.BATCH_SIZE / 2);
        for (let j = 0; j < smallerBatches.length; j++) {
          const smallerBatch = smallerBatches[j];
          if (!smallerBatch) continue;

          const smallerWorkbook = this.createWorkbook(smallerBatch);
          const smallerBuffer = this.generator.writeWorkbook(smallerWorkbook);
          files.push(
            this.generator.createFile(
              smallerBuffer,
              `收件人地址_${getTimestamp()}_${i + 1}_${j + 1}.xlsx`
            )
          );
        }
      } else {
        files.push(this.generator.createFile(buffer, `收件人地址_${getTimestamp()}_${i + 1}.xlsx`));
      }
    }

    return files;
  }

  private createWorkbook(matches: AddressMatch[]): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();
    const data = matches.map((match) => this.formatAddressForExcel(match));
    const worksheet = XLSX.utils.json_to_sheet(data);

    this.generator.freezeFirstRow(worksheet);
    XLSX.utils.book_append_sheet(workbook, worksheet, "收件人地址");

    return workbook;
  }

  private formatAddressForExcel(match: AddressMatch): {
    地址: string;
    邮编: string;
    数量: string;
  } {
    const { address, qsoCount } = match;
    const callInAddress = checkCallInAddress(address.address, address.call);

    const formattedAddress = callInAddress
      ? address.address
      : `${address.address}\n${address.call}`;

    const quantity = qsoCount >= 2 ? qsoCount.toString() : "";

    return {
      地址: formattedAddress,
      邮编: address.zip,
      数量: quantity,
    };
  }
}
